/**
 * API client for conversation management
 */
import { apiFetch } from './apiClient';

/**
 * Create a new conversation
 * @param {string} [title] - Optional conversation title
 * @returns {Promise<Object>} Created conversation
 * @note user_id is determined server-side from settings
 */
export async function createConversation(title = null) {
  const response = await apiFetch('/conversations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title })
  });
  
  if (!response.ok) {
    throw new Error(`Failed to create conversation: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Get all conversations for the current user
 * @param {number} [skip=0] - Number of conversations to skip
 * @param {number} [limit=50] - Maximum conversations to return
 * @returns {Promise<Object>} User conversations with pagination
 * @note user_id is determined server-side from settings
 */
export async function getUserConversations(skip = 0, limit = 50) {
  const params = new URLSearchParams({ skip: skip.toString(), limit: limit.toString() });
  const response = await apiFetch(`/conversations?${params}`);
  
  if (!response.ok) {
    throw new Error(`Failed to get conversations: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Get a specific conversation with messages
 * @param {string} conversationId - Conversation ID
 * @param {number} [skip=0] - Number of messages to skip
 * @param {number} [limit=100] - Maximum messages to return
 * @returns {Promise<Object>} Conversation with messages
 */
export async function getConversationWithMessages(conversationId, skip = 0, limit = 100) {
  const params = new URLSearchParams({ skip: skip.toString(), limit: limit.toString() });
  const response = await apiFetch(`/conversations/${conversationId}?${params}`);
  
  if (!response.ok) {
    throw new Error(`Failed to get conversation: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Delete a conversation
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteConversation(conversationId) {
  const response = await apiFetch(`/conversations/${conversationId}`, {
    method: 'DELETE'
  });
  
  if (!response.ok) {
    throw new Error(`Failed to delete conversation: ${response.status}`);
  }
  
  return response.json();
}
